import { scheduleFromEpoch, createServerClock, selectActiveCampaign, countdown } from '#promotion-schedule';
const Base = globalThis.HTMLElement || class {};
const consumers = new Set();
const percentages = new Set(['5', '10', '15', '20', '50', '85']);
let registry = null;
const guardKey = 'meri:promotion-guard';
// The theme cannot read Shopify automatic discounts on catalog pages, so a cart Shopify has priced
// is the only proof. A campaign the cart contradicts is hidden in this browser until its settings
// change (a new signature) or a later cart shows the discount after all.
function readGuard() {
  try {
    const value = JSON.parse(localStorage.getItem(guardKey) || '{}');
    return value && typeof value === 'object' ? value : {};
  } catch {
    return {};
  }
}
function writeGuard(value) {
  try {
    if (Object.keys(value).length) localStorage.setItem(guardKey, JSON.stringify(value));
    else localStorage.removeItem(guardKey);
  } catch {}
}
// Cart and Liquid amounts are hundredths even for zero-decimal currencies, whose payable unit is then 100.
function currencyDigits(currency) {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
  } catch {
    return 2;
  }
}
// Shopify gave each eligible item at least the advertised saving (other discounts may add more).
// The registry supplies the fixed amount in the same presentment currency as the cart.
// 3.0.0: Shopify truncates each item's discount to the currency's payable unit (a whole yen in JPY), so up to one
// payable unit per item is rounding, not a missing discount: 5% of ¥179,550 is ¥8,977.5 and Shopify takes ¥8,977.
export function cartHonorsCampaign(rule, item, currency) {
  const original = Number(item?.original_line_price);
  const final = Number(item?.final_line_price);
  if (!Number.isFinite(original) || !Number.isFinite(final) || original <= 0) return null;
  const off = original - final;
  const quantity = Number(item.quantity || 1);
  const rounding = quantity * (currencyDigits(currency) === 0 ? 100 : 1);
  if (rule.discountType === 'fixed') {
    const amount = rule.fixedCartAmount;
    if (!Number.isSafeInteger(amount) || amount <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0) return null;
    // A per-item discount is capped at the line's price.
    const expected = Math.min(original, amount * quantity);
    return off > 0 && off + rounding >= expected;
  }
  const percentage = Number(rule.percentage);
  if (!Number.isFinite(percentage) || percentage <= 0) return null;
  return off + rounding >= (original * percentage) / 100;
}
// A campaign applies to specific collections or specific products, like a Shopify discount.
export function campaignScope(element) {
  const ids = (element.dataset.scopeIds ?? '').split(',').filter(Boolean);
  return element.dataset.scope === 'products'
    ? { productIds: ids, collectionIds: [] }
    : { productIds: [], collectionIds: ids };
}
export function campaignMatches(rule, productId, collectionIds = []) {
  return rule.productIds.length
    ? rule.productIds.includes(String(productId))
    : rule.collectionIds.some((id) => collectionIds.includes(id));
}
export function validFixedAmount(amount, currency) {
  if (!/^\d+(?:\.\d{1,3})?$/.test(String(amount)) || !/^[A-Z]{3}$/.test(String(currency))) return false;
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0 || value > Number.MAX_SAFE_INTEGER / 1000) return false;
  try {
    if (!Intl.supportedValuesOf('currency').includes(currency)) return false;
    const formatter = new Intl.NumberFormat('en', { style: 'currency', currency });
    const digits = formatter.resolvedOptions().maximumFractionDigits;
    const decimals = String(amount).split('.')[1] || '';
    return !decimals.slice(digits).replaceAll('0', '');
  } catch {
    return false;
  }
}
// Liquid renders 1234567 whole units in the current market. Reuse that exact money format,
// including its decimals, separators and currency wrapper, independently of the UI language.
function officialMoney(amount, sample) {
  const match = String(sample || '').match(/^(.*?)(1([.,'’\s]?)234(?:\3)567)(?:([.,])(0{1,3}))?([^\d]*)$/u);
  if (!match) return null;
  const digits = (match[5] || '').length;
  const rounded = convertedAmount(amount, 1, digits);
  if (rounded === null) return null;
  const [whole, decimals] = rounded.split('.');
  return match[1] + whole.replace(/\B(?=(\d{3})+(?!\d))/g, match[3]) + (digits ? match[4] + decimals : '') + match[6];
}
function fixedLabel(amount, sample, template) {
  const money = officialMoney(amount, sample);
  return money === null ? null : template.replace(/__AMOUNT__\s+__CURRENCY__/, () => money);
}
export function fixedAmountOffer(amount, currency, moneySample, template = '__AMOUNT__ __CURRENCY__ OFF') {
  return validFixedAmount(amount, currency) ? fixedLabel(amount, moneySample, template) : null;
}
// Multiply decimal inputs before rounding: binary floating point can turn 6 × 0.8875 into 5.32 instead of 5.33.
// Return a decimal string so formatting does not lose a minor unit in a large, otherwise safe amount.
function convertedAmount(amount, rate, digits) {
  const decimal = (value) => {
    const match = String(value)
      .trim()
      .match(/^\+?(\d*)(?:\.(\d*))?(?:e([+-]?\d+))?$/i);
    if (!match || !(match[1] || match[2])) return null;
    return {
      coefficient: BigInt((match[1] || '0') + (match[2] || '')),
      exponent: Number(match[3] || 0) - (match[2] || '').length,
    };
  };
  const left = decimal(amount);
  const right = decimal(rate);
  if (!left || !right) return null;
  const shift = left.exponent + right.exponent + digits;
  const product = left.coefficient * right.coefficient;
  const divisor = shift < 0 ? 10n ** BigInt(-shift) : 1n;
  const scaled = shift < 0 ? product : product * 10n ** BigInt(shift);
  const minor = (scaled + divisor / 2n) / divisor;
  if (minor <= 0n || minor > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  const text = minor.toString().padStart(digits + 1, '0');
  return digits ? `${text.slice(0, -digits)}.${text.slice(-digits)}` : text;
}
// A fixed offer is entered in the store currency. When the shopper pays in another currency, Shopify converts it at the
// current exchange rate at checkout, so the label shows the same conversion in that currency's own decimals, using the
// active currency and rate the storefront exposes as Shopify.currency. Without a usable rate the label is withheld
// rather than shown in the wrong currency.
export function convertedFixedOffer(
  amount,
  storeCurrency,
  active,
  rate,
  moneySample,
  template = '__AMOUNT__ __CURRENCY__ OFF',
) {
  if (!validFixedAmount(amount, storeCurrency)) return null;
  if (!active || active === storeCurrency) return fixedLabel(amount, moneySample, template);
  const factor = Number(rate);
  if (!/^[A-Z]{3}$/.test(String(active)) || !Number.isFinite(factor) || factor <= 0) return null;
  try {
    if (!Intl.supportedValuesOf('currency').includes(active)) return null;
    const formatter = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: active,
      currencyDisplay: 'narrowSymbol',
    });
    const converted = convertedAmount(amount, rate, formatter.resolvedOptions().maximumFractionDigits);
    if (converted === null) return null;
    return fixedLabel(converted, moneySample, template);
  } catch {
    return null;
  }
}
// Compare cart money, not a localized label. Zero-decimal currencies still use hundredths in Shopify money.
export function fixedOfferCartAmount(amount, storeCurrency, active, rate) {
  if (!validFixedAmount(amount, storeCurrency) || !active) return null;
  try {
    if (!Intl.supportedValuesOf('currency').includes(active)) return null;
    const digits = new Intl.NumberFormat('en', { style: 'currency', currency: active }).resolvedOptions()
      .maximumFractionDigits;
    const factor = active === storeCurrency ? 1 : Number(rate);
    if (!Number.isFinite(factor) || factor <= 0) return null;
    const converted = convertedAmount(amount, active === storeCurrency ? 1 : rate, digits);
    if (converted === null) return null;
    const cartDigits = Math.max(2, digits);
    const [whole, decimals = ''] = converted.split('.');
    const value = Number(whole + decimals.padEnd(cartDigits, '0'));
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}
function updateClock(element, campaign, now) {
  if (!campaign) return;
  const digits = countdown(campaign.until - now).split(':');
  element.querySelectorAll('[data-countdown-digit]').forEach((node, index) => {
    if (node.textContent !== digits[index]) node.textContent = digits[index];
  });
}
export class MeriPromotionRegistry extends Base {
  connectedCallback() {
    if (this.cleanup || (registry && registry !== this)) return;
    registry = this;
    this.clock ??= createServerClock(this.dataset.serverNow);
    // Existing Shopify-rendered prices provide presentment currency when the storefront global is absent.
    const currency =
      window.Shopify?.currency?.active ||
      document.querySelector('meri-promotion-price[data-currency]')?.dataset.currency;
    this.currency = /^[A-Z]{3}$/.test(currency || '') ? currency : null;
    this.matches = new Map();
    this.phases = undefined;
    const abort = new AbortController();
    this.rules = [...this.querySelectorAll('[data-promotion-rule]')].slice(0, 8).map((element) => {
      const schedule = scheduleFromEpoch(element.dataset.startEpoch, element.dataset.endEpoch);
      const discountType = element.dataset.discountType || 'percentage';
      const fixedOffer = (template) =>
        discountType === 'fixed'
          ? convertedFixedOffer(
              element.dataset.fixedAmount,
              element.dataset.fixedCurrency,
              this.currency,
              window.Shopify?.currency?.rate,
              this.dataset.moneySample,
              template || undefined,
            )
          : null;
      const fixedLabel = fixedOffer(this.dataset.fixedOffer);
      // 3.0.0 (Owner): on a compare-at sale the campaign is a further discount ("EXTRA … OFF").
      const fixedMoreLabel = fixedLabel && fixedOffer(this.dataset.fixedOfferMore || this.dataset.fixedOffer);
      const validOffer =
        discountType === 'fixed'
          ? Boolean(fixedLabel)
          : discountType === 'percentage' && percentages.has(element.dataset.percentage);
      const template = element.querySelector('template[data-promotion-content]');
      return {
        element,
        template,
        schedule,
        from: Date.parse(schedule?.start),
        until: Date.parse(schedule?.end),
        enabled: element.dataset.enabled === 'true' && validOffer,
        confirmed: element.dataset.confirmed === 'true',
        ...campaignScope(element),
        percentage: element.dataset.percentage,
        discountType,
        fixedLabel,
        fixedMoreLabel,
        fixedCartAmount:
          fixedLabel &&
          fixedOfferCartAmount(
            element.dataset.fixedAmount,
            element.dataset.fixedCurrency,
            this.currency,
            window.Shopify?.currency?.rate,
          ),
        signature: [
          element.dataset.slot,
          discountType,
          element.dataset.percentage,
          element.dataset.fixedAmount,
          element.dataset.fixedCurrency,
          element.dataset.startEpoch,
          element.dataset.endEpoch,
          element.dataset.scope,
          element.dataset.scopeIds,
          this.currency,
        ].join('|'),
      };
    });
    // Forget guards for settings that changed; keep the rest hidden.
    const stored = readGuard();
    const guard = {};
    for (const rule of this.rules) {
      rule.guarded = Boolean(this.currency) && stored[rule.element.dataset.slot] === rule.signature;
      if (rule.guarded) guard[rule.element.dataset.slot] = rule.signature;
    }
    if (JSON.stringify(guard) !== JSON.stringify(stored)) writeGuard(guard);
    this.guardNote = this.parentElement?.querySelector('[data-promotion-guard-warning]');
    this.showGuardNote();
    document.addEventListener('meri:cart:updated', (event) => this.verifyCart(event.detail?.cart), {
      signal: abort.signal,
    });
    const warning = this.parentElement?.querySelector('[data-promotion-config-warning]');
    if (warning)
      warning.hidden = !this.rules.some(
        (rule) => rule.element.dataset.enabled === 'true' && rule.discountType === 'fixed' && !rule.fixedLabel,
      );
    const tick = () => {
      const now = this.clock();
      const phases = this.rules
        .map((rule) =>
          !rule.enabled || !rule.schedule
            ? 'invalid'
            : now < rule.from
              ? 'upcoming'
              : now < rule.until
                ? 'active'
                : 'ended',
        )
        .join(',');
      if (phases !== this.phases) {
        const previous = this.phases;
        this.phases = phases;
        this.matches.clear();
        for (const consumer of consumers) consumer.refresh(now);
        if (previous)
          document.dispatchEvent(new CustomEvent('meri:promotion:phase', { detail: { from: previous, to: phases } }));
      } else for (const consumer of consumers) consumer.tick(now);
    };
    tick();
    // Disabled or unscheduled campaigns never change phase on their own: a
    // settings change re-renders this element. Skip the per-second timer then.
    // A hidden tab shows no countdown: stop the timer and catch up on return.
    const scheduled = this.rules.some((rule) => rule.enabled && rule.schedule);
    let interval = null;
    const run = () => {
      clearInterval(interval);
      interval = scheduled && !document.hidden ? setInterval(tick, 1000) : null;
    };
    run();
    window.addEventListener('pageshow', tick, { signal: abort.signal });
    document.addEventListener(
      'visibilitychange',
      () => {
        tick();
        run();
      },
      { signal: abort.signal },
    );
    this.cleanup = () => {
      abort.abort();
      clearInterval(interval);
    };
    document.dispatchEvent(new CustomEvent('meri:promotion:registry'));
  }
  // 3.0.0 (Owner): a percentage badge names the discount in words; on a compare-at sale it is a further discount.
  percentOffer(percentage, more = false) {
    const template =
      (more ? this.dataset.percentOfferMore : this.dataset.percentOffer) ||
      (more ? 'EXTRA __PERCENT__% OFF' : '__PERCENT__% OFF');
    return template.replace('__PERCENT__', String(percentage));
  }
  match(productId, collectionIds, now, includeGuarded = false) {
    const key = productId + ':' + collectionIds.join(',') + (includeGuarded ? ':all' : '');
    if (!this.matches.has(key))
      this.matches.set(
        key,
        selectActiveCampaign(
          this.rules.map((rule) => ({
            ...rule,
            applicable:
              rule.confirmed && (includeGuarded || !rule.guarded) && campaignMatches(rule, productId, collectionIds),
          })),
          now,
        ),
      );
    return this.matches.get(key);
  }
  // Collection membership is not in cart.js; the page's own price and cart-line markup carry it.
  collectionsFor(productId) {
    for (const node of document.querySelectorAll('[data-product-id][data-collection-ids]'))
      if (node.dataset.productId === String(productId)) return node.dataset.collectionIds.split(',').filter(Boolean);
    return [];
  }
  verifyCart(cart) {
    // A cart priced in another currency cannot establish or clear this page's contradiction.
    if (!this.currency || cart?.currency !== this.currency) return;
    if (!Array.isArray(cart?.items) || !cart.items.length) return;
    // Wait for the cart section that carries collection ids to render.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (registry !== this) return;
        const now = this.clock();
        let changed = false;
        const verified = new Map();
        for (const item of cart.items) {
          if (item.selling_plan_allocation) continue;
          const { campaign } = this.match(item.product_id, this.collectionsFor(item.product_id), now, true) ?? {};
          if (!campaign) continue;
          const honored = cartHonorsCampaign(campaign, item, cart.currency);
          if (honored === null) continue;
          const rule = this.rules.find((candidate) => candidate.element === campaign.element);
          if (!rule) continue;
          verified.set(rule, (verified.get(rule) ?? true) && honored);
        }
        for (const [rule, honored] of verified) {
          if (rule.guarded === !honored) continue;
          rule.guarded = !honored;
          changed = true;
        }
        if (!changed) return;
        const guard = {};
        for (const rule of this.rules) if (rule.guarded) guard[rule.element.dataset.slot] = rule.signature;
        writeGuard(guard);
        this.showGuardNote();
        this.matches.clear();
        for (const consumer of consumers) consumer.refresh(now);
      }),
    );
  }
  showGuardNote() {
    if (!this.guardNote) return;
    const slots = this.rules.filter((rule) => rule.guarded).map((rule) => rule.element.dataset.slot);
    this.guardNote.hidden = !slots.length;
    this.guardNote.textContent = slots.length
      ? (this.guardNote.dataset.template || '__SLOTS__').replace('__SLOTS__', slots.join(', '))
      : '';
  }
  disconnectedCallback() {
    this.cleanup?.();
    this.cleanup = null;
    this.matches?.clear();
    if (registry === this) {
      registry = null;
      for (const consumer of consumers) consumer.refresh(NaN);
      document.dispatchEvent(new CustomEvent('meri:promotion:registry'));
    }
  }
}
export class MeriPromotionView extends Base {
  connectedCallback() {
    this.resize?.disconnect();
    if (['featured', 'collection'].includes(this.dataset.promotionContext)) {
      this.resize = new ResizeObserver(() => {
        const width = this.getBoundingClientRect().width;
        const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        if (width > 0)
          this.style.setProperty('--promotion-layout-scale', String(Math.min(1.25, width / (32.5 * rootSize))));
      });
      this.resize.observe(this);
    }
    consumers.add(this);
    this.refresh(registry?.clock() ?? NaN);
  }
  disconnectedCallback() {
    this.resize?.disconnect();
    consumers.delete(this);
  }
  refresh(now) {
    const ids = (this.dataset.collectionIds ?? '').split(',').filter(Boolean);
    const { campaign } = registry?.match(this.dataset.productId, ids, now) ?? {};
    this.campaign = campaign;
    const source = campaign?.element ?? null;
    if (this.source !== source) {
      this.source = source;
      const initialMatches =
        source && this.dataset.initialCampaign && this.dataset.initialCampaign === source.dataset.slot;
      delete this.dataset.initialCampaign;
      if (!initialMatches) {
        this.replaceChildren();
        if (campaign?.template) {
          this.append(campaign.template.content.cloneNode(true));
        } else this.removeAttribute('aria-label');
      }
    }
    this.hidden = !campaign;
    this.tick(now);
  }
  tick(now) {
    updateClock(this, this.campaign, now);
  }
}
// meri-edition:self-begin
// Self-use edition only: the theme computes the campaign price. The Theme Store edition shows
// Shopify's real price with the campaign badge and checkout hint instead.
// Shopify money values use hundredths even for zero-decimal currencies.
// 3.0.0 (Owner): a compare-at sale no longer excludes the campaign; the campaign comes off the sale price.
export function promotionalPrice(price, percentage, currency, sellingPlan = false) {
  if (!Number.isSafeInteger(price) || price < 0) return null;
  if (sellingPlan || !percentages.has(String(percentage))) return null;
  const digits = currencyDigits(currency);
  const scale = 10 ** Math.max(2, digits);
  const step = digits === 0 ? 100n : 1n;
  // Match Shopify's per-item allocation: truncate the discount, then subtract it.
  const discount = ((BigInt(price) * BigInt(percentage)) / (100n * step)) * step;
  const current = Number(BigInt(price) - discount) / scale;
  return discount > 0n ? { original: price / scale, current, percentage } : null;
}
function computedPromotion(element, campaign) {
  const value =
    campaign &&
    campaign.discountType !== 'fixed' &&
    promotionalPrice(
      Number(element.dataset.priceMax ?? element.dataset.price),
      campaign.percentage,
      element.dataset.currency,
      element.dataset.sellingPlan === 'true',
    );
  // Shopify formats every price in the current market, including editor section renders. The price is copied as the
  // theme's own nodes: each amount stays one unbreakable span, so a narrow row stacks the prices instead.
  const formatted = value ? element.querySelector(`template[data-price-format="${value.percentage}"]`)?.content : null;
  const current = formatted?.querySelector('[data-formatted-price]');
  if (!value || !current?.textContent.trim()) return null;
  const unitValue = promotionalPrice(Number(element.dataset.unitPrice), campaign.percentage, element.dataset.currency);
  return {
    percentage: value.percentage,
    current,
    unit: unitValue ? formatted.querySelector('[data-formatted-unit-price]')?.textContent : null,
    // On a compare-at sale: the whole saving from the struck price, as price.liquid writes the percentage.
    offer: formatted.querySelector('[data-formatted-offer]')?.textContent.trim() || null,
  };
}
// meri-edition:self-end
let badgeSizes;
export class MeriBadge extends Base {
  connectedCallback() {
    badgeSizes ??= new ResizeObserver((entries) => {
      for (const { target, borderBoxSize } of entries) {
        const box = borderBoxSize[0];
        if (box?.inlineSize && box?.blockSize)
          target.style.setProperty('--badge-full-radius', `${Math.min(box.inlineSize, box.blockSize) / 2}px`);
      }
    });
    badgeSizes.observe(this, { box: 'border-box' });
  }
  disconnectedCallback() {
    badgeSizes?.unobserve(this);
  }
}
export class MeriPromotionBadge extends MeriBadge {
  // A product page marks the chosen variant's compare-at sale here when the variant changes.
  static observedAttributes = ['data-compare-sale'];
  connectedCallback() {
    super.connectedCallback();
    consumers.add(this);
    this.refresh(registry?.clock() ?? NaN);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    consumers.delete(this);
  }
  attributeChangedCallback() {
    if (this.isConnected) this.refresh(registry?.clock() ?? NaN);
  }
  refresh(now) {
    const { campaign } =
      registry?.match(this.dataset.productId, (this.dataset.collectionIds ?? '').split(',').filter(Boolean), now) ?? {};
    // A Shopify compare-at price is a sale on its own; a running campaign shows the same badge.
    const sale = Boolean(campaign) || this.hasAttribute('data-compare-sale');
    this.hidden = !sale;
    // Liquid supplies the theme's translated label; 'SALE' is a last resort.
    this.textContent = sale ? this.dataset.title?.trim() || 'SALE' : '';
    const globalAppearance = this.hasAttribute('data-global-appearance');
    if (globalAppearance) {
      const position = registry?.dataset.badgePosition;
      const corner = ['top_left', 'top_right', 'bottom_left', 'bottom_right'].includes(position)
        ? position
        : 'bottom_left';
      const target = this.closest('.product-badges')?.querySelector(`[data-badge-corner="${corner}"]`);
      // Append keeps NEW first if both badges share a corner. The guard avoids reconnect loops.
      if (target && this.parentElement !== target) target.append(this);
    }
  }
  tick() {}
}
export class MeriPromotionPrice extends Base {
  connectedCallback() {
    this.original = this.querySelector('[data-price-original]');
    this.originalValue = this.querySelector('[data-price-original-value]');
    this.current = this.querySelector('[data-price-current]');
    this.badge = this.querySelector('[data-price-percentage]');
    this.campaignBadge = this.querySelector('[data-price-campaign]');
    this.fixed = this.querySelector('[data-price-fixed]');
    this.hint = this.querySelector('[data-price-checkout-hint]');
    if (!this.original || !this.current || !this.badge) return;
    this.unit = this.querySelector('[data-unit-price-current]');
    let initial = false;
    // meri-edition:self-begin
    initial = this.hasAttribute('data-initial-promotion');
    // meri-edition:self-end
    // A compare-at sale keeps its struck price and badge; a campaign only adds its price and a second badge.
    const sale = Number(this.dataset.comparePrice) > Number(this.dataset.price);
    // The real price: a computed campaign strikes it, or keeps it in a template when it stacks on a sale. Prices are
    // kept as copies of their nodes, whose children are written back.
    let current = this.current.cloneNode(true);
    if (initial)
      current = sale
        ? (this.querySelector('template[data-price-baseline]')?.content ?? current)
        : this.originalValue.cloneNode(true);
    this.baseline ??= {
      unit: initial ? this.querySelector('[data-unit-price-original]')?.content.textContent : this.unit?.textContent,
      original: this.originalValue.cloneNode(true),
      current,
      badge: initial && !sale ? '' : this.badge.textContent,
      hidden: sale ? false : initial || this.original.hidden,
      sale,
    };
    consumers.add(this);
    this.refresh(registry?.clock() ?? NaN);
  }
  disconnectedCallback() {
    consumers.delete(this);
  }
  refresh(now) {
    const { campaign } =
      registry?.match(this.dataset.productId, (this.dataset.collectionIds ?? '').split(',').filter(Boolean), now) ?? {};
    if (!this.baseline) return;
    const { sale } = this.baseline;
    const eligible = campaign && this.dataset.sellingPlan !== 'true';
    const fixedLabel =
      eligible && campaign.discountType === 'fixed' ? (sale ? campaign.fixedMoreLabel : campaign.fixedLabel) : null;
    const percentageLabel =
      eligible && campaign.discountType !== 'fixed' && percentages.has(String(campaign.percentage))
        ? registry.percentOffer(campaign.percentage, sale)
        : null;
    let computed = null;
    // meri-edition:self-begin
    computed = eligible ? computedPromotion(this, campaign) : null;
    // meri-edition:self-end
    // A price refreshes when it connects and whenever a campaign changes phase; only a changed value is written, so an
    // unchanged price keeps its nodes. The first time, a value the page already shows (up to white space) is kept as
    // it is. A value is text, or a node whose children are copied in.
    this.written ??= new Map();
    const squash = (text) => String(text).replace(/\s+/g, ' ').trim();
    const write = (node, value) => {
      if (!node || value == null) return;
      const key = squash(typeof value === 'string' ? value : value.textContent);
      if (this.written.get(node) === key) return;
      if (this.written.has(node) || squash(node.textContent) !== key) {
        if (typeof value === 'string') node.textContent = value;
        else node.replaceChildren(...[...value.childNodes].map((child) => child.cloneNode(true)));
      }
      this.written.set(node, key);
    };
    if (this.fixed) {
      this.fixed.hidden = !fixedLabel;
      write(this.fixed, fixedLabel || '');
    }
    write(this.unit, computed?.unit || this.baseline.unit);
    // A sale keeps its compare-at price struck; otherwise a computed campaign strikes the real price.
    write(this.originalValue, computed && !sale ? this.baseline.current : this.baseline.original);
    write(this.current, computed ? computed.current : this.baseline.current);
    this.original.hidden = computed ? false : this.baseline.hidden;
    // Without a computed price the real price stays unstruck; the badge states the campaign offer.
    // 3.0.0 (Owner): a computed campaign on a sale shows one badge, the whole saving from the struck price, and the
    // sale's own badge waits hidden; without a computed price (the Theme Store edition) the campaign is a second
    // badge, "EXTRA 5% OFF".
    const offer = sale && computed?.offer ? registry.percentOffer(computed.offer) : null;
    if (sale) {
      write(this.badge, this.baseline.badge);
      this.badge.hidden = Boolean(offer);
    } else {
      write(this.badge, computed || percentageLabel ? percentageLabel : this.baseline.badge);
      this.badge.hidden = computed || percentageLabel ? false : this.baseline.hidden;
    }
    if (this.campaignBadge) {
      const label = offer || (sale ? percentageLabel : null);
      write(this.campaignBadge, label || '');
      this.campaignBadge.hidden = !label;
    }
    this.current.classList.toggle('price-sale', Boolean(computed) || sale);
    if (this.hint) this.hint.hidden = Boolean(computed) || !(percentageLabel || fixedLabel);
    // Marks a running campaign (Shopify then lists a discount line in the cart), unlike a compare-at sale.
    this.toggleAttribute('data-promotion-active', Boolean(computed || percentageLabel || fixedLabel));
  }
  tick() {}
}
if (globalThis.customElements)
  for (const [name, component] of [
    ['meri-promotion-registry', MeriPromotionRegistry],
    ['meri-promotion-view', MeriPromotionView],
    ['meri-promotion-price', MeriPromotionPrice],
    ['meri-promotion-badge', MeriPromotionBadge],
    ['meri-badge', MeriBadge],
  ])
    if (!customElements.get(name)) customElements.define(name, component);
